/* ==========================================================================
   Collection filters — option 2a companion behaviours.
   Runs alongside theme.js (no theme.js edits). theme.js's event bus is
   internal, so post-render sync is driven by MutationObservers on the
   elements theme.js re-renders ([data-active-filters], [data-results-count]).

   Responsibilities:
   1. Keep the desktop "Filters +" count badge, aria-label, and the bar's
      .filters-active class in sync (chips row visibility, brief §07/§11).
   2. Empty state: when the selection yields 0 results, swap "View 0 items"
      for a "Clear filters" button in the drawer footer (brief §10).
   3. Facet group open/closed state remembered within the session; on first
      visit, the first three facets are expanded (brief §05).
   4. Prune Colour facet values not present in the product_colors metaobject
      (port of the old inline filter-bar script, now null-safe).
   5. Close the sort dropdown on select (brief §04).
   6. Analytics events (brief §12): filter_drawer_open, filter_apply,
      filter_remove, filter_clear_all, sort_change, no_results.
   ========================================================================== */

(function () {
  'use strict';

  var SS_KEY = 'gb-f2a-open-groups';
  var DEFAULT_OPEN_GROUPS = 2;
  var wasEmpty = false;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function getBar() { return $('.filter-bar--2a[data-filter-bar]'); }
  function getDrawer() { return $('.filter-drawer--2a[data-filter-drawer]'); }

  /* ------------------------------------------------------------------
     Analytics
     ------------------------------------------------------------------ */

  function track(action, data) {
    var payload = { event: action };
    if (data) {
      for (var k in data) {
        if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
      }
    }
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(payload);
    }
    try {
      document.dispatchEvent(new CustomEvent('gb:' + action, { detail: data || {} }));
    } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------------------------
     1. Badge / active-state sync
     ------------------------------------------------------------------ */

  // Instant estimate from the drawer's inputs (available before the fetch
  // completes); matches the chip count once theme.js re-renders.
  function checkedCount() {
    var drawer = getDrawer();
    if (!drawer) return 0;
    var count = $$('.filter-item__checkbox:checked', drawer).length;
    var rangeHasValue = $$('[data-range-input]', drawer).some(function (input) {
      return input.value !== '';
    });
    if (rangeHasValue) count += 1;
    return count;
  }

  function chipCount() {
    var bar = getBar();
    if (!bar) return 0;
    return $$('[data-active-filters] [data-remove-filter], [data-active-filters] [data-remove-range]', bar).length;
  }

  function syncBadge() {
    var bar = getBar();
    if (!bar) return;
    var count = checkedCount();

    var badge = $('[data-gb-filter-count]', bar);
    if (badge) {
      badge.textContent = count;
      if (count > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    }

    var trigger = $('[data-gb-filters-open]', bar);
    if (trigger) {
      var base = (trigger.textContent || '').replace(/[+\d]/g, '').trim() || 'Filters';
      trigger.setAttribute('aria-label', count > 0 ? base + ', ' + count + ' applied' : base);
    }
  }

  function syncActiveClass() {
    var bar = getBar();
    if (!bar) return;
    // Chip presence is post-render truth — flips the on-page row (§07)
    bar.classList.toggle('filters-active', chipCount() > 0);
  }

  /* ------------------------------------------------------------------
     2. Zero-results tracking (brief §12). The drawer footer keeps the
     original Clear all / count / Done controls, so no button swapping is
     needed — the count itself reads "No results".
     ------------------------------------------------------------------ */

  function syncEmptyState() {
    var drawer = getDrawer();
    if (!drawer) return;
    var countEl = $('[data-results-count]', drawer);
    if (!countEl) return;

    var parsed = parseInt((countEl.textContent || '').replace(/[^\d]/g, ''), 10);
    var isEmpty = (!parsed || parsed === 0) && checkedCount() > 0;
    if (isEmpty && !wasEmpty) track('no_results');
    wasEmpty = isEmpty;
  }

  /* ------------------------------------------------------------------
     3. Facet group state — session memory + sensible defaults (§05)
     Base theme.css shows/hides the wrapper purely on [aria-hidden], so
     restoring state is attribute-only (no dependency on theme.js timing).
     ------------------------------------------------------------------ */

  function setGroup(toggle, open) {
    var wrapper = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!wrapper) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    wrapper.setAttribute('aria-hidden', open ? 'false' : 'true');
    wrapper.style.display = '';
  }

  function isDesktop() {
    return window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
  }

  function restoreGroups() {
    if (!isDesktop()) return;
    var drawer = getDrawer();
    if (!drawer) return;
    var toggles = $$('[data-drawer-group-toggle]', drawer);
    if (!toggles.length) return;

    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(SS_KEY)); } catch (e) { /* no-op */ }

    if (Array.isArray(saved)) {
      toggles.forEach(function (toggle) {
        var key = toggle.getAttribute('data-drawer-group-toggle');
        setGroup(toggle, saved.indexOf(key) !== -1);
      });
    } else {
      // First visit this session: expand the first few facets, plus any
      // group that already holds an active selection (deep links).
      toggles.forEach(function (toggle, index) {
        var hasActive = !!$('.filter-drawer__group-toggle-count', toggle);
        setGroup(toggle, index < DEFAULT_OPEN_GROUPS || hasActive);
      });
    }
  }

  function saveGroups() {
    if (!isDesktop()) return;
    var drawer = getDrawer();
    if (!drawer) return;
    var open = $$('[data-drawer-group-toggle]', drawer)
      .filter(function (t) { return t.getAttribute('aria-expanded') === 'true'; })
      .map(function (t) { return t.getAttribute('data-drawer-group-toggle'); });
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(open)); } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------------------------
     4. Colour facet pruning (port of the previous inline script)
     Removes Colour options that don't exist in the product_colors
     metaobject map emitted by filter-bar.liquid.
     ------------------------------------------------------------------ */

  function stripWhitespace(input) {
    return String(input || '').replace(/\s|\n/g, '');
  }

  function pruneColorValues() {
    var mapEl = $('[data-gb-color-map]');
    if (!mapEl) return;

    var colors;
    try { colors = JSON.parse(mapEl.textContent); } catch (e) { return; }
    if (!Array.isArray(colors) || !colors.length) return;

    var names = colors.map(function (c) { return stripWhitespace(c.color_name); });

    ['color', 'colour'].forEach(function (handle) {
      var group = document.getElementById('drawer-group-' + handle);
      if (!group) return;
      $$('li.filter-item .filter-item__label', group).forEach(function (label) {
        if (names.indexOf(stripWhitespace(label.textContent)) === -1) {
          var li = label.closest('li');
          if (li) li.remove();
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     5 & 6. Delegated interactions + analytics
     ------------------------------------------------------------------ */

  function onClearAllCapture(e) {
    // The chips-row "Clear filters" link is handled by theme.js's filterBar
    // module, which unchecks inputs in the BAR and serializes it as a form.
    // The 2a bar is a filter-less <div>, so that path throws. Intercept in the
    // capture phase (runs before filterBar's window listener) and route
    // through the drawer's own clear-all machinery instead — it unchecks all
    // drawer inputs, resets the price range, and serializes the drawer form.
    if (!e.target.closest) return;
    var link = e.target.closest('.filter-bar--2a [data-clear-all-filters]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation();

    var drawer = getDrawer();
    var proxy = drawer && $('[data-filter-modal="__tags"] [data-button="clear-all"]', drawer);
    if (proxy) {
      proxy.click(); // clickButton handles tracking via the bubble listener below
    } else if (link.href) {
      window.location.href = link.href; // fallback: hard-navigate to the cleared URL
    }
    window.setTimeout(syncBadge, 60);
  }

  function onDocumentClick(e) {
    var target = e.target;

    var drawerTrigger = target.closest('[data-filter-drawer-target="__tags"]');
    if (drawerTrigger) track('filter_drawer_open');

    var groupToggle = target.closest('.filter-drawer--2a [data-drawer-group-toggle]');
    if (groupToggle) window.setTimeout(saveGroups, 60);

    var chip = target.closest('[data-remove-filter]');
    if (chip) {
      track('filter_remove', { facet: chip.dataset.name || '', value: chip.dataset.value || '' });
      window.setTimeout(syncBadge, 60);
    }

    if (target.closest('[data-remove-range]')) {
      track('filter_remove', { facet: 'price', value: '' });
      window.setTimeout(syncBadge, 60);
    }

    if (target.closest('[data-clear-all-filters], [data-button="clear-all"]')) {
      track('filter_clear_all');
      window.setTimeout(syncBadge, 60);
    }
  }

  function onDocumentChange(e) {
    var input = e.target;
    if (!input) return;

    if (input.classList && input.classList.contains('filter-item__checkbox')) {
      track(input.checked ? 'filter_apply' : 'filter_remove', {
        facet: input.name || '',
        value: input.value || ''
      });
      syncBadge();
    }

    if (input.hasAttribute && input.hasAttribute('data-gb-sort-proxy')) {
      // The bar's sort radios are proxies (the bar is not a form — see
      // filter-bar.liquid). Forward the selection to the drawer form's real
      // sort_by radio; its change handler serializes the full drawer form,
      // so filter params are preserved. Fires immediately (no debounce).
      var drawer = getDrawer();
      if (drawer) {
        var real = $$('input.filter-item__radio[name="sort_by"]', drawer).filter(function (radio) {
          return radio.value === input.value;
        })[0];
        if (real && !real.checked) {
          real.checked = true;
          real.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      // Close the desktop sort dropdown on select (§04): theme.js closes all
      // groups on any click outside a [data-filter-group].
      window.setTimeout(function () { document.body.click(); }, 0);
    }

    if (input.name === 'sort_by') {
      track('sort_change', { value: input.value || '' });
    }

    if (input.matches && input.matches('.filter-drawer--2a [data-range-input]')) {
      syncBadge();
    }
  }

  /* ------------------------------------------------------------------
     Observers — fire after theme.js re-renders chips / counts
     ------------------------------------------------------------------ */

  function observe() {
    var bar = getBar();
    var drawer = getDrawer();

    if (bar) {
      var chips = $('[data-active-filters]', bar);
      if (chips) {
        new MutationObserver(function () {
          syncActiveClass();
          syncBadge();
        }).observe(chips.parentElement || chips, { childList: true, subtree: true });
      }
    }

    if (drawer) {
      var count = $('[data-results-count]', drawer);
      if (count) {
        new MutationObserver(function () {
          syncEmptyState();
        }).observe(count, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */

  function init() {
    if (!getBar() && !getDrawer()) return;
    syncBadge();
    syncActiveClass();
    syncEmptyState();
    restoreGroups();
    pruneColorValues();
    observe();
    document.addEventListener('click', onClearAllCapture, true);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('change', onDocumentChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
